/**
 * Turning a picked photograph into a profile picture.
 *
 * All of it happens in the browser, which is not a privacy flourish here so
 * much as arithmetic: a phone photograph is four megabytes and a profile
 * picture is twenty kilobytes, and sending the first to produce the second
 * would cost a hundred and fifty times the bytes for the same result.
 *
 * Square, centre-cropped, 256px. A square is what every place that shows an
 * avatar reserves, so cropping here means no layout ever has to guess what to
 * do with a portrait.
 */

const SIZE = 256;
const QUALITY = 0.82;

export class AvatarTooLargeError extends Error {
  constructor() {
    super("That picture is too large to use. Try a smaller one.");
    this.name = "AvatarTooLargeError";
  }
}

/**
 * Decode with `createImageBitmap` where it exists, and an `<img>` where it does
 * not. Safari only learned the first for every format fairly recently, and the
 * fallback costs nothing to keep.
 */
async function decode(
  file: File,
): Promise<CanvasImageSource & { width: number; height: number }> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through: an unsupported format here is still worth one more try.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () =>
        reject(new Error("That file could not be read as an image."));
      image.src = url;
    });
    return image;
  } finally {
    // Revoked after the decode, not after the draw: the bitmap is independent
    // of the URL once loaded.
    URL.revokeObjectURL(url);
  }
}

export async function toAvatarDataUrl(file: File): Promise<string> {
  const source = await decode(file);

  const side = Math.min(source.width, source.height);
  const sx = (source.width - side) / 2;
  const sy = (source.height - side) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser cannot prepare the picture.");

  // Better downscaling than the default on a 4000px source, which otherwise
  // aliases into something that looks sharpened and cheap.
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, sx, sy, side, side, 0, 0, SIZE, SIZE);

  if ("close" in source && typeof source.close === "function") source.close();

  const dataUrl = canvas.toDataURL("image/jpeg", QUALITY);

  // The server refuses anything above 120,000 characters. 256px of JPEG is
  // nowhere near it, so this only fires if something is very wrong.
  if (dataUrl.length > 120_000) throw new AvatarTooLargeError();

  return dataUrl;
}
